import { DeleteOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Popconfirm, Space, Table, Tooltip } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { deleteMyday } from '../../fetchHooks/deleteMyday';
import type { MeasurementType, MydayType } from '../../fetchHooks/useMyday.ts';
import { useMydays } from '../../fetchHooks/useMydays.ts';
import { formatDateOnly, getHealthMetricDescription } from '../../utils/utilFunctions.tsx';
import { MyDayVariant } from './MyDayPage.tsx';

interface NotesTableProps {
  setShowEditMydayModal: (item: MydayType) => void;
}

export const NotesTable = ({ setShowEditMydayModal }: NotesTableProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  const { data: mydaysResponse, isLoading: isLoadingMydays } = useMydays(currentPage, pageSize, MyDayVariant.NOTE);
  // Handle both new paginated format { data: [], pagination: {} } and old array format []
  const mydays = Array.isArray(mydaysResponse) ? mydaysResponse : mydaysResponse?.data || [];
  const pagination = Array.isArray(mydaysResponse) ? null : mydaysResponse?.pagination;

  const handleShowMydayDetails = ({ id }: { id: number }) => navigate(`/mydaydetails/${id}`);

  const handleDeleteMyday = (id: number) => {
    deleteMyday(
      id,
      () => {
        queryClient.invalidateQueries({ queryKey: ['useMydays'] });
      },
      true,
    );
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const tablePagination = pagination
    ? {
        current: pagination.page,
        pageSize: pagination.limit,
        total: pagination.total,
        onChange: handlePageChange,
      }
    : undefined;
  const columns = !mydays
    ? []
    : [
        {
          title: t('common.date'),
          dataIndex: 'date',
          defaultSortOrder: 'descend',
          sorter: (a: MydayType, b: MydayType) => a.date.localeCompare(b.date),
          key: 'date',
          render: (date: string, item: MydayType) => (
            <button type="button" className="link-button" onClick={() => handleShowMydayDetails(item)}>
              {formatDateOnly(new Date(date), i18n.language)}
            </button>
          ),
          responsive: ['xs', 'sm', 'lg'],
        },
        {
          title: t('myday.measurements'),
          sorter: (a: MydayType, b: MydayType) => (a.measurements?.length || 0) - (b.measurements?.length || 0),
          dataIndex: 'measurements',
          key: 'measurements',
          render: (measurements: MeasurementType[], item: MydayType) => {
            let mText = '';
            if (measurements?.length)
              measurements?.forEach((item: MeasurementType, index: number) => {
                mText += `${index !== 0 ? ', ' : ''}${getHealthMetricDescription(item.healthMetricType, i18n.language)}`;
              });
            else mText = '-';
            return (
              <Tooltip title={mText}>
                <button
                  type="button"
                  className="link-button"
                  style={{ maxWidth: '10rem' }}
                  onClick={() => handleShowMydayDetails(item)}
                >
                  {mText}
                </button>
              </Tooltip>
            );
          },
          responsive: ['sm'],
        },
        {
          title: t('common.description'),
          dataIndex: 'description',
          sorter: (a: MydayType, b: MydayType) => a.description.localeCompare(b.description),
          key: 'description',
          render: (description: string, item: MydayType) => (
            <button type="button" className="link-button" onClick={() => handleShowMydayDetails(item)}>
              {description || '-'}
            </button>
          ),
          responsive: ['xs', 'sm', 'lg'],
        },
        {
          title: t('myday.files'),
          dataIndex: 'file_count',
          sorter: (a: MydayType, b: MydayType) => a.file_count - b.file_count,
          key: 'file_count',
          render: (files: number, item: MydayType) => (
            <button type="button" className="link-button" onClick={() => handleShowMydayDetails(item)}>
              {files || '-'}
            </button>
          ),
          responsive: ['sm', 'lg'],
        },

        {
          title: t('myday.action'),
          key: 'action',
          render: (_: string, item: MydayType) => (
            <Space.Compact size="middle">
              <Tooltip title={t('myday.openMydayDetails')}>
                <Button
                  shape="round"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => handleShowMydayDetails(item)}
                  className="edit-button"
                />
              </Tooltip>
              <Tooltip title={t('myday.editMydayTooltip')}>
                <Button
                  shape="round"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => setShowEditMydayModal(item)}
                  className="edit-button"
                />
              </Tooltip>
              <Tooltip title={t('myday.deleteMydayTooltip')}>
                <Popconfirm
                  title={t('myday.confirmDelete')}
                  onConfirm={() => handleDeleteMyday(item.id)}
                  onCancel={() => console.log('Canceled')}
                  okText={t('common.yes')}
                  cancelText={t('common.no')}
                >
                  <Button className="edit-button" shape="round" size="small" icon={<DeleteOutlined />} />
                </Popconfirm>
              </Tooltip>
            </Space.Compact>
          ),
          responsive: ['xs', 'sm', 'lg'],
        },
      ];

  return (
    <>
      <Table
        dataSource={isLoadingMydays ? tableSkeleton() : mydays}
        scroll={{ x: true }}
        sticky
        loading={isLoadingMydays}
        bordered
        size="small"
        columns={columns as any}
        rowKey="id"
        style={{ marginBottom: 16 }}
        pagination={tablePagination}
      />
      {t('myday.showingNoteEntries', { count: mydays.length, total: pagination?.total || mydays.length })}
    </>
  );
};

export const tableSkeleton = (length = 10) => {
  return Array.from({ length: length }, (_, i) => ({
    id: `skeleton-${i}`,
    date: new Date().toISOString().split('T')[0], // Use current date as default
    description: '',
    note: '',
    file_count: 0,
    measurements: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    type: MyDayVariant.NOTE,
    user: null,
    files: [],
  })) as unknown as MydayType[];
};
